/* global moment */

/**
 * Dashboard Controller with Admin Area and Settings Feature Configuration
 *
 * This controller supports per-customer customization of admin area and settings features through the company profile's config JSON field.
 * The configuration structure should follow this format:
 *
 * {
 *   "adminArea": {
 *     "users": true,
 *     "partners": true,
 *     "forms": true,
 *     "buildings": true,
 *     "auditTrail": true,
 *     "security": true,
 *     "api": true
 *   },
 *   "settings": {
 *     "personalInformation": true,
 *     "password": true,
 *     "twoFactor": true
 *   }
 * }
 *
 * Note: Features are enabled by default (opt-out model). To disable a feature for a customer, set its value to `false`.
 * If no configuration is found or a specific feature key is missing, the feature will be enabled by default.
 * The `isAdminAreaFeatureEnabled()` helper function uses optional chaining to safely navigate the nested config structure
 * and returns `true` unless the feature is explicitly set to `false`.
 *
 * Example configurations:
 * - Disable buildings feature: { "adminArea": { "buildings": false } }
 * - Disable password settings: { "settings": { "password": false } }
 * - Disable multiple features: { "adminArea": { "buildings": false, "api": false }, "settings": { "two_factor": false } }
 * - All features enabled (default): {} or { "adminArea": {}, "settings": {} }
 */

angular.module('LeasePilot').controller('DashboardController', [
  '$scope',
  '$rootScope',
  '$mdDialog',
  'DocumentService',
  'ShareService',
  'FormService',
  'CompanyService',
  'BuildingService',
  'DownloadService',
  '$http',
  '$timeout',
  '$window',
  function(
    $scope,
    $rootScope,
    $mdDialog,
    DocumentService,
    ShareService,
    FormService,
    CompanyService,
    BuildingService,
    DownloadService,
    $http,
    $timeout,
    $window,
  ) {
    $scope.isLoaded = false;

    $scope.init = async function() {
      $rootScope.$on('loadUser', () => {
        $scope.company = window.user.company;
      });

      $rootScope.forms = await FormService.query({
        testing: window._testing_assets_enabled,
      });
      $rootScope.buildings = await BuildingService.query({
        dashboard: true,
      });
      
      $scope.isLoaded = true;
      $scope.safeApply();

      window.track.event(new LoadDashboardEvent({
        context: $rootScope.getContext(),
      }));
    };

    $scope.showDialog = function(event, isConformedDeal, companyId) {
      companyId = window.user.company.id;

      var template =
        "<md-dialog><new-document-modal company-id='" +
        companyId +
        "' is-conformed-deal='" +
        isConformedDeal +
        "'></new-document-modal></md-dialog>";

      openDialog(template);
    };

    $scope.openDuplicateDialog = function(companyId) {
      companyId = companyId || '';
      var template =
        '<md-dialog>' +
        "<duplicate-company-modal company-id='" +
        companyId +
        "'>" +
        '</duplicate-company-modal></md-dialog>';

      openDialog(template);
    };

    $scope.newCompanyDialog = function() {
      var template =
        '<md-dialog>' +
        '<new-company-modal>' +
        '</new-company-modal></md-dialog>';

      openDialog(template);
    };

    var openDialog = function(template) {
      $mdDialog.show({
        controller:
          ('DialogController', ['$scope', '$mdDialog', DialogController]),
        template: template,
        parent: angular.element(document.body),
        clickOutsideToClose: true,
        fullscreen: false,
      });

      function DialogController($scope, $mdDialog) {
        $scope.cancel = function() {
          $mdDialog.cancel();
        };
      }
    };

    $scope.confirmDeleteCompany = function(companyId, companyName) {
      var template =
        '<md-dialog>' +
        "<delete-company-modal company-id='" +
        companyId +
        "' company-name='" +
        companyName +
        "'>" +
        '</delete-company-modal></md-dialog>';

      openDialog(template);
    };

    $scope.showConfirmDeleteDocument = function(id, type) {
      var buttons = {
        ok: 'OK',
        cancel: 'Cancel',
      };
      $scope.currentLeaseId = id;
      $scope.currentLeaseType = type;
      $scope.openConfirmModal(
        `You are about to permanently delete a document from LeasePilot.
      Press OK to confirm, or Cancel to keep it in the Archive.`,
        () =>
          $scope.deleteDocument($scope.currentLeaseId, $scope.currentLeaseType),
        null,
        buttons,
      );
    };

    window.showConfirmDeleteDocument = $scope.showConfirmDeleteDocument;

    $scope.openConfirmModal = function(
      msg,
      okCallback,
      cancelCallback,
      buttons,
      redirectUrl,
    ) {
      var confirm = $mdDialog.confirm({
        skipHide: true,
        template:
          '<div class="confirm-modal md-dialog-content">' +
          '<p class="title" >' +
          msg +
          '</p>' +
          '<div class="modal-actions">' +
          '<button ng-if=' +
          _.has(buttons, 'ok') +
          ' class="btn blue-outline" ng-click="dialog.hide()">' +
          buttons.ok +
          '</button>' +
          '<button ng-if=' +
          _.has(buttons, 'cancel') +
          ' class="btn blue active" ng-click="dialog.abort()">' +
          buttons.cancel +
          '</button>' +
          '</div>' +
          '</div>',
        onRemoving: function() {
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
        },
      });

      $mdDialog.show(confirm).then(
        function confirmPublish() {
          if (okCallback) okCallback();
        },
        function preventPublish() {
          if (cancelCallback) cancelCallback();
        },
      );
    };

    $scope.showExportErrorsDialog = function(
      event,
      leaseId,
      targetStatus,
      errors,
    ) {
      $mdDialog.show({
        template:
          '<md-dialog><show-export-errors-modal ' +
          "lease-id='" +
          leaseId +
          "' " +
          "target-status='" +
          targetStatus +
          "' " +
          "errors='" +
          JSON.stringify(errors) +
          "'>" +
          '</show-export-errors-modal></md-dialog>',
        parent: angular.element(document.body),
        targetEvent: event,
        clickOutsideToClose: true,
        fullscreen: false,
      });
    };

    window.showExportErrorsDialog = $scope.showExportErrorsDialog;

    $scope.displayLocalDate = function(datetimeString) {
      return moment(datetimeString)
        .add(moment().utcOffset(), 'm')
        .format('MM/DD/YYYY');
    };

    $scope.deleteDocument = function(id, type) {
      DocumentService.get({
        id: id,
        type: type,
      }).then(
        function(doc) {
          if (doc != null) {
            doc.delete().then(function() {
              window.location.reload();
            });
          }
        },
        function(error) {
          console.log(error);
        },
      );
    };

    $scope.selectAccount = function(accountId) {
      // Set cookie server-side to ensure httponly flag is properly set
      $.ajax({
        method: "POST",
        url: "/set_company_id",
        data: { company_id: accountId },
        headers: {
          'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content')
        }
      }).done(function() {
        localStorage.removeItem("dashboard_filter");
        window.location.href = '/';
      }).fail(function(xhr) {
        console.error("Failed to set company_id cookie:", xhr.responseText);
        // Fallback: try setting cookie client-side (without httponly)
        document.cookie = "company_id=" + encodeURIComponent(accountId) + "; path=/; SameSite=Lax" + (window._production ? "; secure" : "");
        localStorage.removeItem("dashboard_filter");
        window.location.href = '/';
      });
    };

    $scope.isSettingsFeatureEnabled = function(featureName) {
      const config = $rootScope.user?.company?.companyProfile?.config;
      if (!config) {
        return true; // Default to enabled
      }
      return config.settings?.[featureName] !== false;
    };

    $scope.isAdminAreaFeatureEnabled = function(featureName) {
      const config = $rootScope.user?.company?.companyProfile?.config;
      if (!config) {
        return true; // Default to enabled
      }
      return config.adminArea?.[featureName] !== false;
    };
  },
]);
